import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Filter, X, Edit2, Plus, ChevronDown } from 'lucide-react';
import axios from 'axios';
import FlagIcon from './FlagIcon';
import Avatar from './Avatar';
import ProjectModal from './ProjectModal';
import AddUpdateModal from './AddUpdateModal';
import { parseProjectValue, formatEUR, formatShort, getProjectValueEUR, fetchExchangeRates } from '../utils/currencyUtils';
import { PaymentProgress } from './PaymentProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PMView = () => {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectUpdates, setProjectUpdates] = useState([]);
  const [hiddenStatuses, setHiddenStatuses] = useState(() => {
    const saved = localStorage.getItem('pmViewHiddenStatuses');
    return saved ? JSON.parse(saved) : [];
  });
  const [filteredOwners, setFilteredOwners] = useState(() => {
    const saved = localStorage.getItem('pmViewFilteredOwners');
    return saved ? JSON.parse(saved) : [];
  });
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [userAvatars, setUserAvatars] = useState({});
  const [projectImages, setProjectImages] = useState([]);
  const [editingUpdateId, setEditingUpdateId] = useState(null);
  const [editingUpdateText, setEditingUpdateText] = useState('');
  const [savingUpdate, setSavingUpdate] = useState(false);
  const [rates, setRates] = useState(null);
  const [visibleUserIds, setVisibleUserIds] = useState([]);

  useEffect(() => {
    if (user && token) {
      fetchStatusOptions();
      fetchProjects();
      fetchUsers();
      fetchRatesAndVisibility();
    }
  }, [user, token]);

  const fetchRatesAndVisibility = async () => {
    const r = await fetchExchangeRates(API, token);
    setRates(r);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const vis = await axios.get(`${API}/settings/user-visibility`, { headers });
      setVisibleUserIds(vis.data?.airports || []);
    } catch (e) {
      setVisibleUserIds([]);
    }
  };

  const refreshUpdatesOnly = async (projectId) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const updatesResponse = await axios.get(`${API}/projects/${projectId}/updates`, { headers });
      setProjectUpdates(updatesResponse.data?.updates || []);
    } catch (error) {
      console.error('Failed to refresh updates:', error);
    }
  };

  // Save filters to localStorage
  useEffect(() => {
    localStorage.setItem('pmViewHiddenStatuses', JSON.stringify(hiddenStatuses));
  }, [hiddenStatuses]);

  useEffect(() => {
    localStorage.setItem('pmViewFilteredOwners', JSON.stringify(filteredOwners));
  }, [filteredOwners]);

  const fetchUsers = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      const users = response.data?.records || response.data || [];
      setAllUsers(users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setAllUsers([]);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/reference-data/project-status`, { headers });
      const statusList = response.data?.records || response.data || [];
      
      // Sort by order field
      const sorted = statusList.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order;
        }
        return (a.field_84_raw || '').localeCompare(b.field_84_raw || '');
      });
      
      setStatusOptions(sorted);
    } catch (error) {
      console.error('Failed to fetch status options:', error);
      setStatusOptions([]);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/projects`, { 
        headers,
        params: { rows_per_page: 1000 }
      });
      const projectsData = response.data.records || [];
      setProjects(projectsData);
      
      // Fetch avatars for project owners
      const ownerIds = [...new Set(
        projectsData
          .map(p => p.field_237_raw?.[0]?.id)
          .filter(Boolean)
      )];
      
      if (ownerIds.length > 0) {
        try {
          const avatarResponse = await axios.post(`${API}/users/avatars`, 
            { user_ids: ownerIds },
            { headers }
          );
          setUserAvatars(avatarResponse.data?.avatars || {});
        } catch (error) {
          console.error('Failed to fetch avatars:', error);
        }
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectDetails = async (projectId) => {
    setLoadingDetails(true);
    setEditingUpdateId(null);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch project details
      const projectResponse = await axios.get(`${API}/projects/${projectId}`, { headers });
      setSelectedProject(projectResponse.data);
      
      // Fetch project updates
      const updatesResponse = await axios.get(`${API}/projects/${projectId}/updates`, { headers });
      setProjectUpdates(updatesResponse.data?.updates || []);

      // Fetch project media (images)
      try {
        const mediaResponse = await axios.get(`${API}/projects/${projectId}/media`, { headers });
        setProjectImages(mediaResponse.data?.images || []);
      } catch (mediaError) {
        setProjectImages([]);
      }
    } catch (error) {
      console.error('Failed to fetch project details:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const startEditUpdate = (update) => {
    setEditingUpdateId(update.id);
    setEditingUpdateText(update.text || '');
  };

  const toggleMilestonePaid = async (index) => {
    if (!selectedProject) return;
    const milestones = (selectedProject.payment_milestones || []).map((m, i) =>
      i === index ? { ...m, paid: !m.paid } : m
    );
    // Optimistic update
    setSelectedProject({ ...selectedProject, payment_milestones: milestones });
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/projects/${selectedProject.id}`, { payment_milestones: milestones }, { headers });
      fetchProjects(); // refresh list barometers
    } catch (error) {
      console.error('Failed to update milestone:', error);
      alert('Failed to update payment status: ' + (error.response?.data?.detail || error.message));
      fetchProjectDetails(selectedProject.id); // revert
    }
  };

  // Color for a milestone's due date: black if not due yet, orange if <=1 month overdue, red if more
  const getMilestoneDateColor = (m) => {
    if (m.paid) return 'text-green-600';
    if (!m.estimated_date) return 'text-gray-400';
    const d = new Date(m.estimated_date);
    if (isNaN(d.getTime())) return 'text-gray-400';
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const days = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'text-gray-900';   // not due yet
    if (days <= 30) return 'text-orange-600'; // overdue up to 1 month
    return 'text-red-600';                    // overdue more than 1 month
  };

  const cancelEditUpdate = () => {
    setEditingUpdateId(null);
    setEditingUpdateText('');
  };

  const saveEditUpdate = async (updateId) => {
    if (!editingUpdateText.trim()) return;
    setSavingUpdate(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/projects/updates/${updateId}`, {
        update_text: editingUpdateText
      }, { headers });
      setEditingUpdateId(null);
      setEditingUpdateText('');
      if (selectedProject) await fetchProjectDetails(selectedProject.id);
    } catch (error) {
      console.error('Failed to save update:', error);
      alert('Failed to save update: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingUpdate(false);
    }
  };

  const calculateDaysSinceUpdate = (project) => {
    // The lastUpdate object comes from the backend with the most recent update
    const lastUpdate = project.lastUpdate;
    
    if (!lastUpdate || !lastUpdate.date) {
      return 'Never';
    }
    
    try {
      const dateStr = lastUpdate.date;
      let lastDate;
      
      if (dateStr.includes('/')) {
        // Format: dd/mm/yyyy
        const [day, month, year] = dateStr.split('/');
        lastDate = new Date(year, month - 1, day);
      } else if (dateStr.includes('-')) {
        // Format: yyyy-mm-dd
        lastDate = new Date(dateStr);
      } else {
        return 'N/A';
      }
      
      const now = new Date();
      const daysDiff = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 ? daysDiff : 'N/A';
    } catch (e) {
      console.error('Date parse error for project:', project.field_2_raw, e);
      return 'N/A';
    }
  };

  const getAgeColor = (days) => {
    if (days === 'Never' || days === 'N/A') return 'text-gray-500';
    if (days <= 7) return 'text-green-600';
    if (days <= 30) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleStatusChange = async (newStatusId) => {
    if (!selectedProject) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      // Find the full status object
      const statusOption = statusOptions.find(s => s.id === newStatusId);
      if (!statusOption) {
        alert('Status not found');
        return;
      }
      
      // Update project status
      await axios.put(`${API}/projects/${selectedProject.id}`, {
        field_85: [statusOption.id],
        field_85_raw: [{ 
          id: statusOption.id, 
          identifier: statusOption.field_84_raw || statusOption.field_84,
          field_84_raw: statusOption.field_84_raw || statusOption.field_84
        }]
      }, { headers });
      
      // Refresh data
      await fetchProjects();
      await fetchProjectDetails(selectedProject.id);
    } catch (error) {
      console.error('Failed to update status:', error);
      if (error.response?.status === 403) {
        alert('Permission denied: You do not have permission to update this project.');
      } else {
        alert('Failed to update project status: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const toggleOwnerFilter = (ownerId) => {
    setFilteredOwners(prev => {
      if (prev.includes(ownerId)) {
        return prev.filter(id => id !== ownerId);
      } else {
        return [...prev, ownerId];
      }
    });
  };

  // Group projects by status
  const groupProjectsByStatus = (projectsList, includeStatuses) => {
    const grouped = {};
    
    // Initialize with status options order
    statusOptions.forEach(status => {
      const statusName = status.field_84_raw;
      if (includeStatuses.includes(statusName)) {
        grouped[statusName] = [];
      }
    });
    
    // Group projects with owner filtering
    projectsList.forEach(project => {
      const status = project.field_85_raw?.[0]?.identifier || 'No Status';
      const ownerId = project.field_237_raw?.[0]?.id;
      
      // Check if project owner is filtered out
      if (filteredOwners.length > 0 && !filteredOwners.includes(ownerId)) {
        return; // Skip this project if owner is filtered out
      }
      
      if (grouped[status]) {
        grouped[status].push(project);
      }
    });
    
    return grouped;
  };

  // Get unique project owners (deduplicated by ID), limited to the Airports section's visible users
  const projectOwners = projects
    .map(p => p.field_237_raw?.[0])
    .filter(o => o && (visibleUserIds.length === 0 || visibleUserIds.includes(o.id)))
    .reduce((unique, owner) => {
      if (!unique.find(u => u.id === owner.id)) {
        unique.push(owner);
      }
      return unique;
    }, [])
    .sort((a, b) => {
      const nameA = a.identifier?.split(' ').slice(1).join(' ') || '';
      const nameB = b.identifier?.split(' ').slice(1).join(' ') || '';
      return nameA.localeCompare(nameB);
    });

  const allStatusNames = statusOptions.map(s => s.field_84_raw);
  const activeStatusNames = allStatusNames.filter(s => !hiddenStatuses.includes(s));
  const filteredStatusNames = hiddenStatuses;

  const activeProjects = groupProjectsByStatus(projects, activeStatusNames);
  const filteredProjects = groupProjectsByStatus(projects, filteredStatusNames);

  const toggleStatusFilter = (statusName) => {
    setHiddenStatuses(prev => {
      if (prev.includes(statusName)) {
        return prev.filter(s => s !== statusName);
      } else {
        return [...prev, statusName];
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] flex">
      {/* Left Side - Projects Table */}
      <div className="w-1/2 border-r border-gray-200 overflow-y-auto p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">PM View</h1>
          
          {/* User Avatars for Owner Filtering */}
          <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">Project Owners:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {projectOwners.map(owner => {
                const isFiltered = filteredOwners.length > 0 && !filteredOwners.includes(owner.id);
                const ownerName = owner.identifier?.split(' ').slice(1).join(' ') || 'Unknown';
                const avatarUrl = userAvatars[owner.id];
                
                return (
                  <button
                    key={owner.id}
                    onClick={() => toggleOwnerFilter(owner.id)}
                    className={`relative group transition ${isFiltered ? 'opacity-50' : ''}`}
                    title={ownerName}
                  >
                    <Avatar 
                      userId={owner.id}
                      userName={ownerName}
                      avatarUrl={avatarUrl}
                      size="md"
                      className="cursor-pointer hover:ring-2 hover:ring-blue-500"
                    />
                    {isFiltered && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-full">
                        <X className="w-6 h-6 text-red-500 stroke-[3]" />
                      </div>
                    )}
                  </button>
                );
              })}
              {filteredOwners.length > 0 && (
                <button
                  onClick={() => setFilteredOwners([])}
                  className="px-3 py-2 text-xs bg-red-100 text-red-700 rounded-full hover:bg-red-200"
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
          
          {/* Status Filters Dropdown */}
          <div className="mb-4">
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">
                  Status Filters {hiddenStatuses.length > 0 && `(${hiddenStatuses.length} hidden)`}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
              
              {showStatusDropdown && (
                <div className="absolute z-10 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                  <div className="p-3 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Filter Statuses</span>
                    {hiddenStatuses.length > 0 && (
                      <button
                        onClick={() => setHiddenStatuses([])}
                        className="text-xs text-blue-600 hover:text-blue-700"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="p-2">
                    {statusOptions.map(status => {
                      const statusName = status.field_84_raw;
                      const isHidden = hiddenStatuses.includes(statusName);
                      return (
                        <label
                          key={status.id}
                          className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={!isHidden}
                            onChange={() => toggleStatusFilter(statusName)}
                            className="rounded border-gray-300"
                          />
                          <span className={`text-sm ${isHidden ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                            {statusName}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Active Projects Table */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Active Statuses</h2>
          {Object.keys(activeProjects).length > 0 ? (
            Object.entries(activeProjects).map(([status, statusProjects]) => (
              statusProjects.length > 0 && (
                <div key={status} className="mb-6">
                  <h3 className="text-md font-semibold text-gray-800 mb-2 bg-gray-100 px-3 py-2 rounded">
                    {status} ({statusProjects.length}) · {formatEUR(statusProjects.reduce((s, p) => s + getProjectValueEUR(p, rates), 0))}
                  </h3>
                  <table className="min-w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">GE</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Age (days)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pay</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {statusProjects.map(project => {
                        const age = calculateDaysSinceUpdate(project);
                        const isSelected = selectedProject?.id === project.id;
                        return (
                          <tr
                            key={project.id}
                            onClick={() => fetchProjectDetails(project.id)}
                            className={`cursor-pointer transition ${
                              isSelected 
                                ? 'bg-blue-100 hover:bg-blue-200' 
                                : 'hover:bg-blue-50'
                            }`}
                          >
                            <td className="px-3 py-2">
                              <FlagIcon countryCode={project.field_117_raw} />
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">{project.field_2_raw || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{project.field_5_raw || 'Untitled'}</td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900" title={formatEUR(getProjectValueEUR(project, rates))}>
                              {getProjectValueEUR(project, rates) > 0 ? formatShort(getProjectValueEUR(project, rates)) : '-'}
                            </td>
                            <td className={`px-3 py-2 text-sm font-medium ${getAgeColor(age)}`}>
                              {age === 'Never' || age === 'N/A' ? age : `${age}d`}
                            </td>
                            <td className="px-3 py-2">
                              {project.payment_milestones?.length > 0 ? (
                                <PaymentProgress milestones={project.payment_milestones} variant="compact" />
                              ) : (
                                <span className="text-xs text-gray-300">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ))
          ) : (
            <p className="text-gray-500 text-center py-8">No active projects</p>
          )}
        </div>

        {/* Filtered Projects Table */}
        {Object.keys(filteredProjects).length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-gray-600 mb-4">Filtered Statuses</h2>
            {Object.entries(filteredProjects).map(([status, statusProjects]) => (
              statusProjects.length > 0 && (
                <div key={status} className="mb-6">
                  <h3 className="text-md font-semibold text-gray-600 mb-2 bg-gray-50 px-3 py-2 rounded">
                    {status} ({statusProjects.length}) · {formatEUR(statusProjects.reduce((s, p) => s + getProjectValueEUR(p, rates), 0))}
                  </h3>
                  <table className="min-w-full opacity-60">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Flag</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">GE</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Value</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Age (days)</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pay</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {statusProjects.map(project => {
                        const age = calculateDaysSinceUpdate(project);
                        const isSelected = selectedProject?.id === project.id;
                        return (
                          <tr
                            key={project.id}
                            onClick={() => fetchProjectDetails(project.id)}
                            className={`cursor-pointer transition ${
                              isSelected 
                                ? 'bg-blue-100 hover:bg-blue-200' 
                                : 'hover:bg-blue-50'
                            }`}
                          >
                            <td className="px-3 py-2">
                              <FlagIcon countryCode={project.field_117_raw} />
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900">{project.field_2_raw || '-'}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{project.field_5_raw || 'Untitled'}</td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900" title={formatEUR(getProjectValueEUR(project, rates))}>
                              {getProjectValueEUR(project, rates) > 0 ? formatShort(getProjectValueEUR(project, rates)) : '-'}
                            </td>
                            <td className={`px-3 py-2 text-sm font-medium ${getAgeColor(age)}`}>
                              {age === 'Never' || age === 'N/A' ? age : `${age}d`}
                            </td>
                            <td className="px-3 py-2">
                              {project.payment_milestones?.length > 0 ? (
                                <PaymentProgress milestones={project.payment_milestones} variant="compact" />
                              ) : (
                                <span className="text-xs text-gray-300">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )
            ))}
          </div>
        )}
      </div>

      {/* Right Side - Project Details */}
      <div className="w-1/2 overflow-y-auto p-6 bg-gray-50">
        {loadingDetails ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : selectedProject ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">{selectedProject.field_5_raw || 'Project Details'}</h2>
              <button
                onClick={() => setShowProjectModal(true)}
                className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            </div>
            
            {/* Project Image */}
            {(selectedProject.featured_image || projectImages.length > 0) && (
              <div className="mb-4" data-testid="pm-view-project-image">
                <img
                  src={selectedProject.featured_image || projectImages[0].url}
                  alt={selectedProject.field_5_raw || 'Project'}
                  className="w-full h-48 object-cover rounded-lg shadow"
                />
              </div>
            )}

            {/* Project Info */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">GE Code</p>
                  <p className="font-medium">{selectedProject.field_2_raw || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Status</p>
                  <select
                    value={selectedProject.field_85_raw?.[0]?.id || ''}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                  >
                    {statusOptions.map(status => (
                      <option key={status.id} value={status.id}>
                        {status.field_84_raw}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Country</p>
                  <div className="flex items-center gap-2">
                    <FlagIcon countryCode={selectedProject.field_117_raw} />
                    <p className="font-medium">{selectedProject.field_83_raw?.[0]?.identifier || '-'}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Value</p>
                  <p className="font-medium">{getProjectValueEUR(selectedProject, rates) > 0 ? formatEUR(getProjectValueEUR(selectedProject, rates)) : '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Owner</p>
                  <p className="font-medium">{selectedProject.field_237_raw?.[0]?.identifier || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Deadline</p>
                  <p className="font-medium">{selectedProject.field_9 || '-'}</p>
                </div>
              </div>
              
              {selectedProject.field_247_raw && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-500 mb-2">Summary</p>
                  <p className="text-sm text-gray-700">{selectedProject.field_247_raw}</p>
                </div>
              )}
            </div>

            {/* Payment Progress */}
            {selectedProject.payment_milestones?.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4 mb-4" data-testid="pm-view-payment-progress">
                <h3 className="font-semibold text-gray-900 mb-3">Payment Progress</h3>
                <PaymentProgress milestones={selectedProject.payment_milestones} variant="bar" />
                <div className="mt-3 space-y-2">
                  {selectedProject.payment_milestones.map((m, i) => (
                    <div key={m.id || i} className="flex items-center justify-between text-xs gap-2">
                      <label className="flex items-center gap-2 cursor-pointer min-w-0" data-testid={`pm-milestone-paid-toggle-${i}`}>
                        <input
                          type="checkbox"
                          checked={!!m.paid}
                          onChange={() => toggleMilestonePaid(i)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                          data-testid={`pm-milestone-paid-checkbox-${i}`}
                        />
                        <span className="font-medium text-gray-700 flex-shrink-0">{m.percentage}%</span>
                        {m.notes && <span className="text-gray-500 truncate">— {m.notes}</span>}
                      </label>
                      <span className={`font-medium flex-shrink-0 ${getMilestoneDateColor(m)}`}>{m.estimated_date || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Add Update Button */}
            <div className="mb-4">
              <button
                onClick={() => setShowAddUpdateModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                <Plus className="w-4 h-4" />
                Add Update
              </button>
            </div>

            {/* Update History */}
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="font-semibold text-gray-900 mb-4">Update History</h3>
              {projectUpdates.length > 0 ? (
                <div className="space-y-3">
                  {projectUpdates.map((update, index) => {
                    const isEditing = editingUpdateId === update.id;
                    const hasAuthor = update.author && !['Unknown', 'System'].includes(update.author);
                    return (
                      <div key={update.id || index} className="border-l-2 border-blue-500 pl-4 pb-3 group" data-testid={`pm-view-update-${index}`}>
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {update.date && <span className="font-medium">{update.date}</span>}
                            {update.time && <span>{update.time}</span>}
                            {hasAuthor && (
                              <>
                                <span>•</span>
                                <span className="text-gray-700 font-medium">{update.author}</span>
                              </>
                            )}
                          </div>
                          {!isEditing && update.id && (
                            <button
                              onClick={() => startEditUpdate(update)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-blue-600 transition"
                              title="Edit update"
                              data-testid={`pm-view-edit-update-btn-${index}`}
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                        {isEditing ? (
                          <div className="space-y-2">
                            <textarea
                              value={editingUpdateText}
                              onChange={(e) => setEditingUpdateText(e.target.value)}
                              rows={3}
                              className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                              autoFocus
                              data-testid={`pm-view-edit-update-input-${index}`}
                            />
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => saveEditUpdate(update.id)}
                                disabled={savingUpdate || !editingUpdateText.trim()}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                data-testid={`pm-view-save-update-btn-${index}`}
                              >
                                {savingUpdate ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={cancelEditUpdate}
                                disabled={savingUpdate}
                                className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                data-testid={`pm-view-cancel-update-btn-${index}`}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-700 whitespace-pre-line">{update.text || 'No update text'}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No updates yet</p>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <p>Select a project from the list to view details</p>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProjectModal && selectedProject && (
        <ProjectModal
          project={selectedProject}
          onClose={() => setShowProjectModal(false)}
          onSave={() => {
            setShowProjectModal(false);
            fetchProjects();
            fetchProjectDetails(selectedProject.id);
          }}
        />
      )}

      {showAddUpdateModal && selectedProject && (
        <AddUpdateModal
          project={selectedProject}
          onClose={() => setShowAddUpdateModal(false)}
          onSave={() => {
            setShowAddUpdateModal(false);
            refreshUpdatesOnly(selectedProject.id);
          }}
        />
      )}
    </div>
  );
};

export default PMView;
